<script setup
        lang="ts">
withDefaults(defineProps<{
    text: string;
    icon?: string;
    iconPosition?: 'left' | 'right';
    highlight?: boolean;
    danger?: boolean;
    loading?: boolean;
    disabled?: boolean;
    shrink?: boolean;
    success?: boolean;
}>(), {
    iconPosition: 'left',
    highlight:    false,
    danger:       false,
    loading:      false,
    disabled:     false,
    shrink:       true,
    success:      false,
});

const emit = defineEmits<{
    (e: 'click'): void;
}>();
</script>

<template>
    <button :class="[
                'h-7',
                'ml-2 px-2',
                'border-0 rounded-md',
                'shadow-square',
                'text-sm font-helvetica whitespace-nowrap select-none',
                'transition-colors duration-200 ease-in-out',
                'group',
                'focus-visible:outline-none',
                {
                    'cursor-pointer': !disabled && !loading,
                    'cursor-not-allowed': disabled,
                    'cursor-wait': loading,
                },
                {
                    'bg-black-800 text-white-600 hover:bg-black-700 hover:text-white-500': !highlight && !success && !danger && !disabled && !loading,
                    'bg-black-400 text-white-800 hover:bg-black-400 hover:text-white-800': !highlight && (disabled || loading),
                    'bg-primary-700 text-black-800 hover:bg-primary-500 hover:text-black-900': highlight,
                    'bg-primary-900 text-black-800 hover:bg-primary-900 hover:text-black-800': highlight && (disabled || loading),
                    'bg-success-700 text-black-800 hover:bg-success-500 hover:text-black-900': success,
                    'bg-success-900 text-black-800 hover:bg-success-900 hover:text-black-800': success && (disabled || loading),
                    'bg-error-700 text-white-600 hover:bg-error-600 hover:text-white-600': danger,
                    'bg-error-900 text-black-800 hover:bg-error-900 hover:text-black-800': danger && (disabled || loading),
                },
            ]"
            :disabled="disabled"
            @click="(!disabled && !loading) && emit('click')">
        <span v-if="icon && iconPosition === 'left'"
              :class="[
                  'align-middle',
                  'size-4',
                  'mr-0 mobile-l:mr-1',
                  {
                      'text-white-600 group-hover:text-white-500': !highlight && !success && !danger && !disabled && !loading,
                      'text-white-800 group-hover:text-white-800': !highlight && (disabled || loading),
                      'text-black-800 group-hover:text-black-700': highlight || success,
                      'text-black-400 group-hover:text-black-400': highlight || success && (disabled || loading),
                      'text-white-600 group-hover:text-white-600': danger,
                      'text-error-900 group-hover:text-error-900': danger && (disabled || loading),
                  }
              ]">
            <i :class="[
                loading ? 'i-svg-spinners:3-dots-scale' : icon,
                'text-xs leading-none',
                'transition-colors duration-200 ease-in-out',
            ]" />
        </span>
        <span :class="[
            'transition-colors duration-200 ease-in-out',
            {
                'hidden mobile-l:inline-flex': shrink,
                'inline-flex': !shrink,
            }
        ]">{{ text }}</span>
        <span v-if="icon && iconPosition === 'right'"
              :class="[
                  'align-middle',
                  'size-4',
                  'ml-0 mobile-l:ml-1',
                  {
                      'text-white-600 group-hover:text-white-500': !highlight && !success && !danger && !disabled && !loading,
                      'text-white-800 group-hover:text-white-800': disabled || loading,
                      'text-black-800 group-hover:text-black-700': highlight,
                      'text-primary-900 group-hover:text-primary-900': highlight && (disabled || loading),
                      'text-success-700 group-hover:text-success-700': success,
                      'text-success-900 group-hover:text-success-900': success && (disabled || loading),
                      'text-error-700 group-hover:text-error-700': danger,
                      'text-error-900 group-hover:text-error-900': danger && (disabled || loading),
                  }
              ]">
            <i :class="[
                loading ? 'i-svg-spinners:3-dots-scale' : icon,
                'text-xs leading-none',
                'transition-colors duration-200 ease-in-out',
            ]" />
        </span>
    </button>
</template>
